import * as React from "react";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
} from "@floating-ui/react";
import { StatusIndicator } from "../display/StatusIndicator";
import { CloseIcon } from "@salt-ds/icons";

import { Button } from "../controls/Button";
import { H2, Text } from "../display/Text";
import { useMatchedBreakpoints } from "../layout/useBreakpoint";
import "./dialog.css";

/**
 * The Dialog family — Dialog, DialogHeader, DialogContent, DialogActions, DialogCloseButton and
 * Scrim — replacing `@salt-ds/core`'s across `dialog.tsx` and `alert-dialog.tsx`.
 *
 * ── WHY IT KEEPS RENDERING AFTER open GOES FALSE ──────────────────────────────────────
 *
 * `open` is the caller's intent; `showComponent` is what is on screen. When `open` flips to
 * false the dialog STAYS MOUNTED for the exit animation and unmounts on `animationend` — with a
 * 300ms timeout as a backstop, because `animationend` never fires if the animation was disabled
 * by `prefers-reduced-motion` or by a test environment. Unmount immediately and the dialog
 * vanishes rather than closing; rely on the event alone and it can never close at all.
 *
 * ── THE SCRIM IS ALSO THE SCROLL LOCK ─────────────────────────────────────────────────
 *
 * `FloatingOverlay lockScroll` both dims the page and stops it scrolling behind the dialog.
 * Those are one concern: a modal you can scroll away from is not modal, and the page jumping to
 * the top when the scrollbar disappears is the usual botched version of this — which
 * `FloatingOverlay` handles by compensating for the scrollbar width.
 *
 * ── AN OVERFLOWING BODY BECOMES A FOCUSABLE REGION ────────────────────────────────────
 *
 * When `DialogContent` overflows it gains `role="region"`, `tabIndex={0}` and a label. Without
 * that, a keyboard user cannot scroll the dialog body at all: there is nothing focusable inside
 * it to receive arrow keys, and the scrollbar is not reachable.
 */
export type DialogSize = "small" | "medium" | "large";
export type DialogStatus = "info" | "error" | "warning" | "success";

interface DialogContextValue {
  status?: DialogStatus;
  id?: string;
  setId?: (id: string) => void;
  dialogId?: string;
}

const DialogContext = React.createContext<DialogContextValue>({});

export function useDialogContext(): DialogContextValue {
  return React.useContext(DialogContext);
}

export interface ScrimProps extends React.HTMLAttributes<HTMLDivElement> {
  fixed?: boolean;
  open?: boolean;
}

export const Scrim = React.forwardRef<HTMLDivElement, ScrimProps>(function Scrim(
  { children, className, fixed = false, open = true, ...rest },
  ref,
) {
  if (!open) return null;
  const classes = ["saltScrim"];
  if (fixed) classes.push("saltScrim-fixed");
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")} data-testid="scrim" ref={ref} {...rest}>
      {children}
    </div>
  );
});

export interface DialogProps extends React.HTMLAttributes<HTMLDivElement> {
  /*
   * `role` is deliberately NOT omitted, matching the vendor: `alert-dialog.tsx` passes
   * `role="alertdialog"`, which is a different promise to a screen reader — it announces the
   * dialog's contents immediately rather than waiting to be read. `...rest` is spread after the
   * default so a caller's role wins.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  size?: DialogSize;
  status?: DialogStatus;
  disableDismiss?: boolean;
  disableScrim?: boolean;
  initialFocus?: number | React.MutableRefObject<HTMLElement | null>;
  /** The vendor's alias for `id`; kept so no call site has to change. */
  idProp?: string;
}

const ORDER = ["xs", "sm", "md", "lg", "xl"] as const;

export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  {
    "aria-labelledby": ariaLabelledBy,
    children,
    className,
    disableDismiss,
    disableScrim,
    id,
    idProp,
    initialFocus,
    onOpenChange,
    open = false,
    size = "medium",
    status,
    ...rest
  },
  ref,
) {
  const generatedId = React.useId();
  const dialogId = id ?? idProp ?? generatedId;
  const matched = useMatchedBreakpoints();
  // The largest matched breakpoint is the current one. Empty is legal — the test harness stubs
  // matchMedia false — and `xs` is the honest answer then, not a crash.
  const currentBreakpoint = matched.length ? matched[matched.length - 1] : ORDER[0];

  const [showComponent, setShowComponent] = React.useState(false);
  const [headerId, setHeaderId] = React.useState<string | undefined>(undefined);

  const { context, refs } = useFloating({ open: showComponent, onOpenChange });
  const { getFloatingProps } = useInteractions([
    useClick(context),
    // `outsidePress` is what a click on the scrim does. Turning it off is how a dialog that
    // must be answered stops being dismissable by a stray click.
    useDismiss(context, { outsidePress: !disableDismiss }),
  ]);
  const mergedRef = useMergeRefs([refs.setFloating, ref]);

  React.useEffect(() => {
    if (open && !showComponent) {
      setShowComponent(true);
      return;
    }
    if (!open && showComponent) {
      // Backstop for the `animationend` handler below: with animations disabled the event never
      // arrives, and without this the dialog would stay on screen forever.
      const animate = setTimeout(() => setShowComponent(false), 300);
      return () => clearTimeout(animate);
    }
  }, [open, showComponent]);

  const contextValue = { status, id: headerId, setId: setHeaderId, dialogId };

  if (!showComponent) return <DialogContext.Provider value={contextValue} />;

  const classes = ["saltDialog", `saltDialog-${size}-${currentBreakpoint}`];
  classes.push(open ? "saltDialog-enterAnimation" : "saltDialog-exitAnimation");
  if (status) classes.push(`saltDialog-${status}`);
  if (className) classes.push(className);

  const dialog = (
    <FloatingFocusManager context={context} initialFocus={initialFocus} modal outsideElementsInert>
      <div
        aria-labelledby={[ariaLabelledBy, headerId].filter(Boolean).join(" ") || undefined}
        aria-modal="true"
        className={classes.join(" ")}
        id={dialogId}
        onAnimationEnd={() => {
          if (!open && showComponent) setShowComponent(false);
        }}
        role="dialog"
        {...getFloatingProps()}
        {...rest}
        ref={mergedRef}
      >
        {children}
      </div>
    </FloatingFocusManager>
  );

  return (
    <DialogContext.Provider value={contextValue}>
      <FloatingPortal>
        {disableScrim ? (
          dialog
        ) : (
          // `FloatingOverlay` is both the scrim and the scroll lock — one concern, not two.
          <FloatingOverlay className="saltScrim saltScrim-fixed" data-testid="scrim" lockScroll>
            {dialog}
          </FloatingOverlay>
        )}
      </FloatingPortal>
    </DialogContext.Provider>
  );
});

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  actions?: React.ReactNode;
  description?: React.ReactNode;
  disableAccent?: boolean;
  header?: React.ReactNode;
  preheader?: React.ReactNode;
  status?: DialogStatus;
}

export const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
  function DialogHeader(
    { actions, className, description, disableAccent, header, preheader, status: statusProp, ...rest },
    ref,
  ) {
    const { status: statusContext, setId, id: contextHeaderId } = useDialogContext();
    const status = statusProp ?? statusContext;
    const generatedId = React.useId();
    const headerId = contextHeaderId ?? generatedId;

    React.useEffect(() => {
      // The header publishes its id upward so the DIALOG can point `aria-labelledby` at it.
      // Without this the dialog is announced with no name at all.
      if (headerId) setId?.(headerId);
    }, [headerId, setId]);

    const classes = ["saltDialogHeader"];
    if (!disableAccent && !status) classes.push("saltDialogHeader-withAccent");
    if (status) classes.push(`saltDialogHeader-${status}`);
    if (className) classes.push(className);

    return (
      <div className={classes.join(" ")} ref={ref} {...rest}>
        {status ? <StatusIndicator status={status} /> : null}
        <div className="saltDialogHeader-container">
          <H2 className="saltDialogHeader-header" id={headerId}>
            {preheader ? <Text color="primary">{preheader}</Text> : null}
            {header}
          </H2>
          {/*
            No `saltDialogHeader-description` class. The vendor emits one and NOTHING defines it
            — not salt-core.css, not the component's own injected stylesheet. Same call as
            `saltSwitch-icon` earlier in this migration: dead vocabulary the guard caught the
            moment it was copied across, with no visual consequence because it styled nothing.
          */}
          {description ? <Text color="secondary">{description}</Text> : null}
        </div>
        {actions ? <div className="saltDialogHeader-actionsContainer">{actions}</div> : null}
      </div>
    );
  },
);

export type DialogContentProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent({ children, className, ...rest }, ref) {
    const [canScrollUp, setCanScrollUp] = React.useState(false);
    const [canScrollDown, setCanScrollDown] = React.useState(true);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const divRef = React.useRef<HTMLDivElement | null>(null);
    const { dialogId, id: headerId } = useDialogContext();

    const handleScroll = () => {
      requestAnimationFrame(() => {
        const container = divRef.current;
        if (!container) return;
        setCanScrollUp(container.scrollTop > 0);
        setCanScrollDown(
          container.scrollHeight - container.scrollTop - container.clientHeight > 1,
        );
      });
    };

    const checkOverflow = React.useCallback(() => {
      const container = divRef.current;
      if (!container) return;
      setIsOverflowing(
        container.scrollWidth > container.offsetWidth ||
          container.scrollHeight > container.offsetHeight,
      );
    }, []);

    React.useLayoutEffect(() => {
      checkOverflow();
      const container = divRef.current;
      if (!container || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(checkOverflow);
      observer.observe(container);
      return () => observer.disconnect();
    }, [checkOverflow]);

    /*
     * Only when it actually overflows. A scrollable region has to be focusable or a keyboard
     * user cannot scroll it — there is nothing inside to take the arrow keys and the scrollbar
     * is not reachable. Adding tabIndex unconditionally would instead put a useless stop in the
     * tab order of every dialog that fits.
     */
    const overflowProps = isOverflowing
      ? { role: "region", tabIndex: 0, "aria-labelledby": headerId ?? dialogId }
      : {};

    const inner = ["saltDialogContent-inner"];
    if (isOverflowing) inner.push("saltDialogContent-overflow");
    if (isOverflowing && canScrollUp) inner.push("saltDialogContent-scrollTop");
    if (isOverflowing && canScrollDown) inner.push("saltDialogContent-scrollBottom");

    return (
      <div
        className={className ? `saltDialogContent ${className}` : "saltDialogContent"}
        ref={ref}
        {...rest}
      >
        <div className={inner.join(" ")} onScrollCapture={handleScroll} ref={divRef} {...overflowProps}>
          {children}
        </div>
      </div>
    );
  },
);

export type DialogActionsProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogActions = React.forwardRef<HTMLDivElement, DialogActionsProps>(
  function DialogActions({ children, className, ...rest }, ref) {
    return (
      <div
        className={className ? `saltDialogActions ${className}` : "saltDialogActions"}
        ref={ref}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

export type DialogCloseButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

export const DialogCloseButton = React.forwardRef<HTMLButtonElement, DialogCloseButtonProps>(
  function DialogCloseButton({ className, ...rest }, ref) {
    return (
      <Button
        appearance="transparent"
        aria-label="Close dialog"
        className={className ? `saltDialogCloseButton ${className}` : "saltDialogCloseButton"}
        ref={ref}
        {...rest}
      >
        <CloseIcon aria-hidden />
      </Button>
    );
  },
);
